import { supabase, isSupabaseConfigured } from './client';

export const auth = {
  /**
   * Real Supabase sign in with email and password
   */
  async signIn(email, password) {
    if (!isSupabaseConfigured) {
      throw new Error('Supabase credentials not configured in environment.');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      throw error;
    }

    // Verify admin role in database
    const isAdmin = await this.verifyAdmin(data.user?.id);
    if (!isAdmin) {
      await supabase.auth.signOut();
      throw new Error('Access denied. Your account is not authorized as an administrator.');
    }

    return { user: data.user, session: data.session };
  },

  /**
   * Real Supabase sign out
   */
  async signOut() {
    if (!isSupabaseConfigured) return;
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },

  /**
   * Get current active session
   */
  async getSession() {
    if (!isSupabaseConfigured) return null;
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Get session error:', error);
      return null;
    }
    return session;
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback) {
    if (!isSupabaseConfigured) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    return supabase.auth.onAuthStateChange(callback);
  },

  /**
   * Verify whether the authenticated user exists in the admins table and is active
   */
  async verifyAdmin(userId) {
    if (!isSupabaseConfigured || !userId) return false;

    try {
      const { data, error } = await supabase
        .from('admins')
        .select('id, role, is_active')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Verify admin database error:', error);
        return false;
      }

      return Boolean(data && data.is_active);
    } catch (err) {
      console.error('Verify admin exception:', err);
      return false;
    }
  },
};

export default auth;
